import { cloneDeep } from "lodash-es";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";

import type { MomentumItem } from "./MomentumItem";
import type { CurveFormType } from "../relation/RelationItem";
import type { GraphPoint } from "../relation/GraphPoint";

export const momentumStructure: MomentumItem = {
  type: "momentum",
  id: 0,
  uid: "momentum_0",

  elementType: "target",
  elementId: -1,

  description: "",
  imageId: null,

  interval: 0,

  curvePoints: [],
  curveType: "linear",

  formula: "1 * x",

  docs: {},
  createdAt: 0
};

export const useMomentumStore = defineItemStore(
  "model/momentums",
  momentumStructure,
  {
    getters: {
      byElementUid(state): { [uid: string]: MomentumItem } {
        const result: { [uid: string]: MomentumItem } = {};

        for (const id in state.items) {
          const item = state.items[id];
          const elementUid = `${item.elementType}_${item.elementId}`;
          result[elementUid] = item;
        }
        
        return result;
      },
      byTarget(state): { [targetId: string]: MomentumItem } {
        const result: { [targetId: string]: MomentumItem } = {};

        for (const id in state.items) {
          const item = state.items[id];
          if (item.elementType != "target") continue;
          result[item.elementId] = item;
        }
        
        return result;
      },
      byType(state): { [type: string]: { [id: number]: MomentumItem} } {
        const result: { [type: string]: { [id: number]: MomentumItem} } = {};

        for (const id in state.items) {
          const item = state.items[id];
          if (result[item.elementType] == null) {
            result[item.elementType] = {};
          }
          result[item.elementType][item.elementId] = item;
        }
        
        return result;
      }
    },
    events: {
      async beforeCreate(data) {
        if (data.elementType == null) {
          throw new Error("Element type is required");
        }
        if (data.elementId == null) {
          throw new Error("Element id is required");
        }

        const items = {
          target: useTargetStore().items,
          resource: useResourceStore().items
        };

        const element = items[data.elementType][data.elementId];
        const { minValue, maxValue } = element;

        if (minValue != null && maxValue != null) {
          data.curvePoints = [
            {
              id: 0,
              x: minValue,
              y: 0,
              bezierOut: {
                x: 0,
                y: 0
              }
            },
            {
              id: 1,
              x: maxValue,
              y: 0,
              bezierIn: {
                x: 0,
                y: 0
              }
            }
          ];
        } else if (minValue == null && maxValue == null) {
          data.curvePoints = [{
            id: 0,
            x: 0,
            y: 0,
            bezierIn: { x: 0, y: 0 },
            bezierOut: { x: 0, y: 0 }
          }];
        } else {
          const min = minValue ?? maxValue! - 100;
          const max = maxValue ?? minValue! + 100;
          data.curvePoints = [
            {
              id: 0,
              x: min,
              y: 0,
              bezierOut: {
                x: 0,
                y: 0
              }
            },
            {
              id: 1,
              x: max,
              y: 0,
              bezierIn: {
                x: 0,
                y: 0
              }
            }
          ];
        }
        return data;
      },
      async afterDelete(momentum): Promise<unknown> {

        if (momentum.elementType === "target") {
          // Targets have a momentumActive property that needs to be updated
          const targetStore = useTargetStore();
          targetStore.update({
            id: momentum.elementId,
            momentumActive: false
          });
        }

        return true;
      }
    },
    actions: {
      /**
       */
      updateCurve(options: {
        id: number;
        curveType: CurveFormType;
        curvePoints: GraphPoint[];
      }) {
        const { id, curveType, curvePoints } = options;
        const relation = this.items[id];

        // we seperate the update to the curve in two sets.

        // set 1: curve data (points, factor and type)
        const dataToChange = {
          id,
          curvePoints: cloneDeep(curvePoints),
          curveType: relation.curveType
        };

        if (relation.curveType !== curveType) {
          dataToChange.curveType = curveType;
        }

        this.update(dataToChange);
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useMomentumStore, import.meta.hot)
  );
}
